/**
 * Helper functions for calendar and make-up session operations
 * Converted from reference project App/Themes/Helpers.js
 */

/**
 * Compares if both schedule options are disabled
 * @param obj - Object with allow_schedule_option_one and allow_schedule_option_two properties
 * @returns true if both options are false
 */
export function compareAllow(obj: any): boolean {
  return (
    obj.allow_schedule_option_one === false &&
    obj.allow_schedule_option_two === false
  );
}

/**
 * Compares two objects by their id property
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @returns true if both objects have the same id
 */
export function compareObjects(obj1: any, obj2: any): boolean {
  return obj1.id === obj2.id;
}

/**
 * Compares two date objects
 * @param a - First date object
 * @param b - Second date object
 * @returns Difference in milliseconds (negative if a < b, positive if a > b, 0 if equal)
 */
export function compareDates(a: any, b: any): number {
  return new Date(a.date).getTime() - new Date(b.date).getTime();
}

/**
 * Merges arrays from responseUpdate (existing make-up session) and response (new timeslots)
 * Used when rescheduling make-up sessions to combine existing selections with new available timeslots
 * @param responseUpdate - Response from getMake API containing existing make-up session data
 * @param response - Response from getTimeslots API containing available timeslots
 * @returns Merged, deduplicated, and sorted array of timeslots
 */
export function mergeArray(responseUpdate: any, response: any): any[] {
  let arrayListMake: any[] = [];
  
  // Process checked make-up session schedules from responseUpdate
  if (responseUpdate?.data?.data?.checked_make_up_session_schedules) {
    responseUpdate.data.data.checked_make_up_session_schedules.forEach((el: any) => {
      let a = response.data?.data?.find((object: any) => object.id !== el.id);
      if (a) {
        arrayListMake.push(el);
      } else {
        arrayListMake.push({
          ...el,
          ...{ allow_schedule_option_one: true, allow_schedule_option_two: true },
        });
      }
    });
  }
  
  // Process new timeslots from response
  if (response.data?.data) {
    response.data.data.forEach((el: any) => {
      let a = arrayListMake.find((object: any) => object.id !== el.id);
      if (a) {
        arrayListMake.push(el);
      } else {
        arrayListMake.push({
          ...el,
          ...{ allow_schedule_option_one: true, allow_schedule_option_two: true },
        });
      }
    });
  }
  
  // Remove duplicates based on id
  let uniqueArray = arrayListMake.filter((item, index, self) => {
    return self.findIndex((obj) => compareObjects(obj, item)) === index;
  });
  
  // Filter out items with both options disabled
  let uniqueArray2 = uniqueArray.filter((item: any) => {
    if (
      item.allow_schedule_option_one === false &&
      item.allow_schedule_option_two === false
    ) {
      return false;
    }
    return true;
  });
  
  // Sort by date
  return uniqueArray2.sort(compareDates);
}

export function isValidValue(value: string | null | undefined): boolean {
  if (!value) return false;
  if (value === 'null' || value === 'undefined') return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}
